"use client"

import TEDatePicker from "@/components/common/TEDatePicker"
import Text from "@/components/common/Typography/Text"
import NightIcon from "@/components/icons/NightIcon"
import { useAppDispatch } from "@/lib/redux/hooks"
import { hotelActions } from "@/lib/redux/features/hotel/reduce"

import { Card, Col, Row, theme } from "antd"
import { FC, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs, { type Dayjs } from "dayjs"

type SelectDateProps = {
  size?: 'small' | 'default',
  value: [any, any]
}

const parseDate = (value: any): Dayjs | null => {
  if (value == null || value === '') return null
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value)
  return parsed.isValid() ? parsed : null
}

const DateFieldDisplay: FC<{
  date: Dayjs | null
  isSmall: boolean
  selected: boolean
  placeholder: string
  format: string
}> = ({ date, isSmall, selected, placeholder, format }) => (
  // Plain text instead of antd DatePicker — avoids SingleSelector SSR/CSR className mismatch.
  <div
    className={
      isSmall
        ? `home__searching--hero-dates-value${selected ? ' home__searching--date-selected' : ''}`
        : `home__searching--input${selected ? ' home__searching--date-selected' : ''}`
    }
    suppressHydrationWarning
  >
    {date ? date.format(format) : placeholder}
  </div>
)

const SelectRangeDate: FC<SelectDateProps> = ({ size = 'default', value }) => {
  const { token } = theme.useToken()
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const isSmall = size === 'small'
  const [selector, setSelector] = useState<'from' | 'to' | undefined>()
  const { t, i18n } = useTranslation()
  const start = parseDate(value[0])
  const end = parseDate(value[1])
  const hasCheckIn = !!start
  const hasCheckOut = !!end
  const nightCount = start && end ? (end.diff(start, 'day') || 1) : 1
  const placeholder = t('common.select_date', { defaultValue: 'Chọn ngày' })
  const dateFormat = isSmall
    ? (i18n.language?.startsWith("vi") ? "ddd, D [Thg] M YYYY" : "ddd, DD MMM YYYY")
    : "DD/MM/YYYY"

  return (
    <TEDatePicker
      minRangeLen={1}
      variant="flight"
      popupProps={{
        placement: 'bottomRight'
      }}
      value={start && end ? [start, end] : undefined}
      onChange={(value) => {
        value[0] && dispatch(hotelActions.selectDateCheckIn(value[0]))
        value[1] && dispatch(hotelActions.selectDateCheckOut(value[1]))
      }}
      range
      selector={selector}
    >
      {isSmall ? (
        <div
          ref={ref}
          className="home__searching--hero-dates-block home__searching--hero-dates-block--compact"
        >
          <div className="home__searching--hero-dates-bar home__searching--hero-dates-bar--compact">
            <div
              className="home__searching--hero-dates-segment home__searching--hero-dates-segment--from home__searching--hero-dates-segment--active"
              onClick={() => setSelector('from')}
              role="presentation"
            >
              <div className="home__searching--hero-dates-segment-content home__searching--hero-dates-segment-content--compact">
                <div className="home__searching--field-box home__searching--field-box--compact">
                  <Text className="home__searching--field-label">{t("hotel.check_in")}</Text>
                  <div className="home__searching--hero-dates-value-row">
                    <DateFieldDisplay
                      date={start}
                      isSmall
                      selected={hasCheckIn}
                      placeholder={placeholder}
                      format={dateFormat}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="home__searching--hero-dates-divider" aria-hidden />
            <div
              className="home__searching--hero-dates-segment home__searching--hero-dates-segment--to home__searching--hero-dates-segment--active"
              onClick={() => setSelector('to')}
              role="presentation"
            >
              <div className="home__searching--hero-dates-segment-content home__searching--hero-dates-segment-content--compact">
                <div className="home__searching--field-box home__searching--field-box--compact">
                  <Text className="home__searching--field-label">{t("hotel.check_out")}</Text>
                  <div className="home__searching--hero-dates-value-row">
                    <DateFieldDisplay
                      date={end}
                      isSmall
                      selected={hasCheckOut}
                      placeholder={placeholder}
                      format={dateFormat}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Card
          ref={ref}
          size='small'
          variant='borderless'
          className="home__searching--placement"
        >
          <Row gutter={[30,30]} wrap={false} align='middle' justify='space-between'>
            <Col flex={1} onClick={() => setSelector('from')}>
              <Row className="home__searching--input-label" align='middle' gutter={[5, 5]} wrap={false}>
                <Col>
                  <Text>{t("hotel.check_in")}</Text>
                </Col>
              </Row>
              <Row style={{ paddingBlock: '0.5rem' }}>
                <DateFieldDisplay
                  date={start}
                  isSmall={false}
                  selected={hasCheckIn}
                  placeholder={placeholder}
                  format={dateFormat}
                />
              </Row>
              <Row>
                <Text
                  ellipsis
                  type={hasCheckIn ? undefined : 'secondary'}
                  style={hasCheckIn ? { color: token.colorPrimary } : { visibility: 'hidden', opacity: 0 }}
                >
                  {start ? start.format("dddd, YYYY") : '\u00a0'}
                </Text>
              </Row>
            </Col>
            <Col>
              <Row justify='center'>
                <NightIcon style={{ color: token.colorPrimary as string, fontSize: '2.2rem' }} />
              </Row>
              <Row>
                <Text style={{ whiteSpace: 'nowrap' }}>
                  {nightCount} {t("common.night")}
                </Text>
              </Row>
            </Col>
            <Col flex={1} onClick={() => setSelector('to')}>
              <Row className="home__searching--input-label" align='middle' gutter={[5, 5]} wrap={false}>
                <Col>
                  <Text>{t("hotel.check_out")}</Text>
                </Col>
              </Row>
              <Row style={{ paddingBlock: '0.5rem' }}>
                <DateFieldDisplay
                  date={end}
                  isSmall={false}
                  selected={hasCheckOut}
                  placeholder={placeholder}
                  format={dateFormat}
                />
              </Row>
              <Row>
                <Text
                  ellipsis
                  type={hasCheckOut ? undefined : 'secondary'}
                  style={hasCheckOut ? { color: token.colorPrimary } : { visibility: 'hidden', opacity: 0 }}
                >
                  {end ? end.format("dddd, YYYY") : '\u00a0'}
                </Text>
              </Row>
            </Col>
          </Row>
        </Card>
      )}
    </TEDatePicker>
  )
}

export default SelectRangeDate
